/**
 * @fileOverview The walkthrough's words as the dialog shows them — the step's text, a view's short
 * name, the "moving on to" line, a speed in the reader's number format. Shared by the dialog's parts
 * so each says a thing the same way.
 */

import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useViewHelp } from "./useViewHelp"

export function useViewHelpCopy() {
  const { t, te, locale } = useI18n()
  const { tour, step, steps, displayIndex, nextRoute, speed, textOf, isRelatedSection, currentSection, frameTrail } =
    useViewHelp()

  const tourTitle = computed(() => (tour.value ? t(`help.tour.${tour.value.key}.title`) : ""))
  const text = computed(() => textOf(step.value))

  /** A view's own short name, for the header, the timeline and the "moving on to" line. */
  const sectionLabel = (name?: string | null): string => {
    if (!name || !tour.value) return ""
    const key = `help.tour.${tour.value.key}.section.${name}`
    return te(key) ? t(key) : ""
  }

  /** What the tour calls its related views, together — "Zusammenhänge". */
  const chapterLabel = computed(() => (tour.value ? t(`help.tour.${tour.value.key}.related`) : ""))

  /**
   * A view's name where it heads something — the dialog's header, the timeline's peek. A related view
   * carries the chapter's name in front, so a settings page never reads as a seventh tab.
   */
  const sectionHeading = (name?: string | null): string => {
    const label = sectionLabel(name)
    return name && label && isRelatedSection(name) ? `${chapterLabel.value} · ${label}` : label
  }

  /**
   * Where the step being read is, coarsest first: the chapter a related view belongs to, the view
   * itself, then what the frame says — the record whose page it is, the tab open on it. The header
   * draws it as a trail, which answers "where does this happen" in the product's own words; the
   * address `/employee/demo-1#company` would answer it in the developers' words, with a sample id
   * the reader could go looking for and never find.
   */
  const trail = computed<string[]>(() => {
    // The section decides the chapter, the view names the page: a section can span two views (the
    // Zeiterfassung's Meine Zeiten and Zeiten prüfen), and the trail says which of them this is.
    const section = currentSection.value
    const fromFrame = frameTrail.value
    const view = sectionLabel(steps.value[displayIndex.value]?.route)

    // The catalog's name for a view is usually an area — Mitarbeiter, Projekt — and belongs in front
    // of what the frame says. Sometimes it is a tab instead (the walkthrough calls a settings page by
    // the tab it is about), and then the frame already carries it, in the right order, behind the page
    // it sits on: "Einstellungen › Arbeitszeitmodelle", not "Arbeitszeitmodelle › Einstellungen".
    const named = fromFrame.some(crumb => crumb.toLowerCase() === view.toLowerCase())

    return [section && isRelatedSection(section) ? chapterLabel.value : "", named ? "" : view, ...fromFrame].filter(
      Boolean
    )
  })

  /**
   * When the next step is on another view, the text says so — so that when the pointer then clicks a
   * tab and the page changes, the reader was told it would.
   */
  const continueHint = computed(() => {
    const destination = sectionLabel(nextRoute.value)
    return destination ? t("help.hint.continue_to", { view: destination }) : ""
  })

  const dotLabel = computed(() => `${text.value} ${t("help.hint.next")}`)

  /** A speed as the reader's locale writes it: "1,25×" in German, "1.25×" in English. */
  const formatSpeed = (value: number) =>
    `${new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(value)}×`

  const speedLabel = computed(() => formatSpeed(speed.value))

  return {
    t,
    tourTitle,
    text,
    sectionLabel,
    sectionHeading,
    chapterLabel,
    trail,
    continueHint,
    dotLabel,
    formatSpeed,
    speedLabel,
  }
}
